import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { StepperContext } from "../../../stepper/context/StepperContext";
import { StepSubmitter } from "../../../stepper/StepSubmitter";
import { Box, CircularProgress, Typography } from "@mui/material";
import TitleBar from "../../../components/common-components/TitleBar";
import { useDispatch } from "react-redux";
import {
  createUpdateItin,
  createUpdateItinW7,
  createUpdateItinW848,
  getItinData,
} from "../../../stores/itin";
import F2848Form from "../../../components/itin/w2484form/F2848Form";
import { useLocation, useNavigate, useParams } from "react-router";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ApiHelperFunction from "../../../api";
import { useStepData } from "../../../stepper/UseStepData";
import { useInputHandler } from "../../../stepper/hooks/useInouthandler";
import { setToast } from "../../../stores/toast/toastslice";

const getScrollableParent = (node: HTMLElement | null): HTMLElement | null => {
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return node;
    }
    node = node.parentElement;
  }
  return document.scrollingElement as HTMLElement;
};
export const Step4 = () => {
  const stepperState = useContext(StepperContext);

  const { payload, handleFormChange } = useInputHandler<any>({});
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode =
    location.pathname.includes("edit-documents") ||
    location.pathname.includes("edit-w7");
  const { id } = useParams<{ id: string }>();
  const { data } = useStepData();
  const [showData, setShowData] = useState<any>();

  const step3Data = stepperState.data.get(2) || {};
  const extractedData = data ?? {};
  const [form2848Values, setForm2848Values] = useState<any>({
    ...step3Data,
    ...extractedData,
  });
  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        const response = await dispatch<any>(
          getItinData({ order_id: id }),
        ).unwrap();
        setShowData(response);
        // console.log("API Response:", response?.has_us_company);
      } catch (error) {
        console.error("Error fetching ITIN data:", error);
      }
    };

    fetchData();
  }, [id]);

  // The 2848 is captured as two sections — the form through line 5a, and the
  // page-2 running head onwards — so each starts a fresh PDF page instead of
  // the whole form being squashed onto one sheet.
  const generatePDF = async (): Promise<Blob> => {
    const sectionIds = ["pdf-content-page-1", "pdf-content-page-2"];
    // US Letter, the Form 2848 sheet. Each #pdf-content-page-* div is laid out
    // at exactly 612 x 792pt with the official's margins inside the artwork, so
    // a section maps onto a PDF page 1:1 and edge-to-edge. Do NOT force a width
    // here: the page is F2848_CAPTURE_WIDTH_PX wide, and squeezing it to some
    // other value compresses every run horizontally.
    const PAGE_W_MM = 215.9;
    const PAGE_H_MM = 279.4;
    const pdf = new jsPDF("p", "mm", "letter", true);
    let addedPage = false;

    const root = document.getElementById("pdf-content");
    const previousZoom = root ? root.style.getPropertyValue("zoom") : "";

    // The capture zooms #pdf-content from its on-screen 0.68 scale back to 1,
    // which visibly grows the live element. A fixed-position spinner overlay
    // covers it for the duration (rendered below), so the grow/shrink happens
    // behind the overlay instead of in view. Note: this must stay a plain
    // position:fixed overlay and NOT clip the wrapper itself (e.g. via
    // overflow:hidden) — html2canvas respects ancestor overflow clipping, so
    // constraining the live element's ancestor box during capture would crop
    // the rendered canvas instead of just hiding it visually.
    setCapturing(true);

    if (root) {
      // the sheets are scaled down on screen; capture has to run at 1:1
      root.style.setProperty("zoom", "1");
      root.classList.add("is-capturing");
    }

    try {
      for (const sectionId of sectionIds) {
        const input = document.getElementById(sectionId);
        if (!input) continue;

        const canvas = await html2canvas(input, { scale: 2, useCORS: true });
        if (addedPage) pdf.addPage();
        pdf.addImage(
          canvas.toDataURL("image/png"),
          "PNG",
          0,
          0,
          PAGE_W_MM,
          PAGE_H_MM,
          undefined,
          "FAST",
        );
        addedPage = true;
      }
    } finally {
      if (root) {
        if (previousZoom) root.style.setProperty("zoom", previousZoom);
        else root.style.removeProperty("zoom");
        root.classList.remove("is-capturing");
      }
      setCapturing(false);
    }

    if (!addedPage) throw new Error("PDF content not found!");

    return pdf.output("blob");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const pdfBlob = await generatePDF();
      const pdfFile = new File([pdfBlob], "form_2848.pdf", {
        type: "application/pdf",
      });

      // The browser-rendered 2848 is uploaded under form_2848_file, matching
      // the W-7 (`w7_form: pdfFile`) and SS-4 (`ein_form_file: pdfFile`) flows.
      // The old `form_2848: "submitted"` signal, which made the API store its
      // own static template instead, is still accepted by the backend for
      // backward compatibility but is no longer sent from here.
      const apiPayload = {
        form_2848_file: pdfFile,
        order_id: id,
        ...(isEditMode && { is_updating: "true" }),
      };

      const response = await dispatch<any>(
        createUpdateItinW848(apiPayload),
      ).unwrap();

      // The thunk resolves with the API body, so status/message sit at its top
      // level (same shape UploadSignComonentItin reads).
      if (response?.status === 1) {
        dispatch<any>(
          setToast({
            message: response?.message,
            type: "success",
            title: "Success",
          }),
        );

        setTimeout(() => {
          navigate(`/itin-detail/${id}`);
        });
      }

      if (response?.status === 0) {
        dispatch<any>(
          setToast({
            message: response?.message,
            type: "error",
            title: "Error",
          }),
        );
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollableParent = getScrollableParent(rootRef.current);
    if (scrollableParent) {
      scrollableParent.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [stepperState.currentStepIdx]);
  return (
    <div ref={rootRef}>
      <TitleBar
        title="Fill the 2848 form"
        description="Complete the 2848 form for ITIN application."
      />

      <form onSubmit={handleSubmit} onChange={handleFormChange}>
        {/* field changes reach handleFormChange by bubbling to the
            surrounding <form onChange>, exactly as before */}
        <Box ref={wrapperRef} sx={{ position: "relative" }}>
          <F2848Form value={showData} loading={loading} />
          {capturing && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                bottom: 0,
                // only covers the content column to the right of the sidebar,
                // not the full viewport, so the sidebar stays visible
                left: wrapperRef.current?.getBoundingClientRect().left ?? 0,
                right: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                bgcolor: "#fff",
                zIndex: 1300,
              }}
            >
              <CircularProgress />
              <Typography variant="body1" color="text.secondary">
                Preparing your form…
              </Typography>
            </Box>
          )}
        </Box>
        <StepSubmitter
          label={"Submit"}
          showPrevious={true}
          showNext={true}
          loading={loading}
        />
      </form>
    </div>
  );
};
